import { useState } from "react";
import { addDays, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarDays, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import {
  useDocumentFilters,
  type AddedDateFilterValue,
} from "../../context/DocumentFiltersContext";

type Preset = AddedDateFilterValue["preset"];

/** Compact white chips with a grey border. */
const PRESET_CLASS = "h-7 rounded-md border-border bg-white px-2.5 text-xs font-normal";

/** Local-calendar days → the half-open `created_at` range the queries take. */
export function addedRangeFromDays(from: Date, to: Date): { from: string; to: string } {
  const [start, end] = from <= to ? [from, to] : [to, from];
  return {
    from: startOfDay(start).toISOString(),
    // Exclusive: the start of the day after the last one picked.
    to: startOfDay(addDays(end, 1)).toISOString(),
  };
}

/** The last `days` calendar days, today included, open-ended so uploads keep arriving. */
export function recentAddedRange(days: number, today = new Date()): { from: string } {
  return { from: startOfDay(addDays(today, -(days - 1))).toISOString() };
}

function formatDay(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * "Added" — filters documents by when they reached Franklin (`created_at`),
 * whatever date the Timeline places them by. Shared by every Documents tab.
 */
export function AddedDateFilter() {
  const { t, language } = useLanguage();
  const copy = t.documents.addedFilter;
  const locale = language === "et" ? "et-EE" : "en-GB";
  const { filters, setAddedDate } = useDocumentFilters();
  const value = filters.addedDate;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();

  const commit = (next: AddedDateFilterValue | null) => {
    setAddedDate(next);
    setOpen(false);
  };

  const label = (() => {
    if (!value) return copy.anyTime;
    if (value.preset === "7d") return copy.last7Days;
    if (value.preset === "30d") return copy.last30Days;
    const { from, to } = value.range;
    // `to` is exclusive: the last day shown is the day before it.
    const lastDay = to ? addDays(new Date(to), -1).toISOString() : undefined;
    return `${from ? formatDay(from, locale) : "…"} – ${lastDay ? formatDay(lastDay, locale) : "…"}`;
  })();

  const applyCustom = () => {
    if (!draft?.from) return;
    commit({ preset: "custom", range: addedRangeFromDays(draft.from, draft.to ?? draft.from) });
  };

  const choosePreset = (preset: Exclude<Preset, "custom">) =>
    commit({ preset, range: recentAddedRange(preset === "7d" ? 7 : 30) });

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(
            value?.preset === "custom" && value.range.from
              ? {
                  from: new Date(value.range.from),
                  to: value.range.to ? addDays(new Date(value.range.to), -1) : undefined,
                }
              : undefined
          );
        }
      }}
    >
      <div
        className={cn(
          "inline-flex h-9 w-max shrink-0 overflow-hidden rounded-md border border-input bg-background transition-colors",
          value && "border-[#ef4129] bg-[#fbeee9]"
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${copy.title}: ${label}`}
            className={cn(
              "flex h-full shrink-0 items-center gap-1.5 px-2.5 text-[11px] font-medium hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none",
              value && "hover:bg-[#fbeee9]"
            )}
          >
            <CalendarDays
              className={cn("h-3.5 w-3.5 shrink-0 opacity-60", value && "text-[#ef4129]")}
              aria-hidden="true"
            />
            <span className="whitespace-nowrap">
              {copy.title}: {label}
            </span>
          </button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            onClick={() => commit(null)}
            aria-label={copy.clear}
            title={copy.clear}
            className="flex w-8 shrink-0 items-center justify-center bg-[#fbeee9] text-[#a93420] hover:bg-[#f7dfd7] focus-visible:bg-[#f7dfd7] focus-visible:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <PopoverContent align="end" className="w-auto p-0">
        <div className="space-y-2 border-b px-3 py-2.5">
          <p className="text-xs font-semibold">{copy.title}</p>
          <p className="text-xs text-muted-foreground">{copy.hint}</p>
          <div className="flex flex-wrap gap-1">
            <Button
              variant="outline"
              size="sm"
              className={PRESET_CLASS}
              onClick={() => commit(null)}
            >
              {copy.anyTime}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={PRESET_CLASS}
              onClick={() => choosePreset("7d")}
            >
              {copy.last7Days}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={PRESET_CLASS}
              onClick={() => choosePreset("30d")}
            >
              {copy.last30Days}
            </Button>
          </div>
        </div>
        <div className="flex justify-center">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={setDraft}
            defaultMonth={draft?.from}
            disabled={{ after: new Date() }}
            numberOfMonths={1}
          />
        </div>
        <div className="flex justify-end gap-2 border-t px-3 py-2">
          <Button size="sm" onClick={applyCustom} disabled={!draft?.from}>
            {copy.apply}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
